/**
 * Deriving the four smart-start cards from ONE daily-plan payload —
 * ROUTE_INVENTORY §2.11 #24, #25, #26 and #27.
 *
 * The grid used to fire a `/practice/queue` preview per card, so the four
 * flipped state on four different clocks and the screen assembled itself in
 * front of the reader. Now every card reads from the same payload and the set
 * reveals together. That makes the derivation a RULE rather than a render
 * detail, and rules belong where they can be tabled: the fallbacks below are
 * the interesting part, and none of them is visible in a screenshot.
 *
 * Pure, and in `src/lib` so it stays that way — no hooks, no React.
 */

import type { DailyPlanSummary, StarterPack } from '@/types/api';
import type { PracticeMode } from './session-types';

/** Per-concept time estimates, in seconds. Quiz costs more than a flashcard. */
export const SECONDS_PER_CONCEPT: Record<PracticeMode, number> = {
  flashcard: 20,
  quiz: 45,
};

export interface StarterCounts {
  /** `null` means "not known yet" — distinct from a known zero. */
  due: number | null;
  weak: number | null;
  starred: number | null;
  recent: number | null;
  recentLessonId?: string;
  recentLessonTitle?: string;
}

/**
 * Counts for the four cards, with the bucket fallbacks the payload needs.
 *
 * `starter_packs` is optional: older payloads and some fixtures carry only
 * `buckets`. Due and weak can be reconstructed from those buckets, so they
 * degrade to a number; starred and recent cannot be reconstructed from
 * anything, so they stay `null` and their cards render as "not known" rather
 * than as a confident zero. That difference is the whole reason `null` and `0`
 * are kept apart — a card showing 0 disables itself, and disabling a card
 * because the payload was an older shape would be silently wrong.
 */
export function deriveStarterCounts(
  plan: DailyPlanSummary | undefined
): StarterCounts {
  const packs = plan?.starter_packs;
  const dueFallback = plan
    ? plan.buckets.must_practice + plan.buckets.should_practice
    : null;
  return {
    due: packs?.due?.count ?? dueFallback,
    weak: packs?.weak?.count ?? plan?.buckets.must_practice ?? null,
    starred: packs?.starred?.count ?? null,
    recent: packs?.recent?.count ?? null,
    recentLessonId: packs?.recent?.lesson_id ?? undefined,
    recentLessonTitle: packs?.recent?.lesson_title ?? undefined,
  };
}

/** The first two concept names a card previews. */
export function previewOf(pack: StarterPack | undefined): string[] {
  return (pack?.preview ?? []).slice(0, 2);
}

/**
 * §2.11 #26 — estimated minutes for a scope, in the current mode.
 *
 * `undefined` for an unknown or empty count, so the card renders no estimate
 * at all rather than "~0 min". Rounds UP to a floor of one minute: a session
 * that exists takes some time, and "~0 min" reads as "instant".
 */
export function estimateMinutes(
  count: number | null | undefined,
  mode: PracticeMode
): number | undefined {
  if (count == null || count <= 0) return undefined;
  return Math.max(1, Math.round((count * SECONDS_PER_CONCEPT[mode]) / 60));
}

/**
 * §2.11 #27 — whether the Review section has nothing left in it.
 *
 * Weak is a subset of due, so both at zero means the queue is clear. Gated on
 * the plan having LOADED, so the "All caught up" chip cannot flash before the
 * payload resolves — congratulating someone on an empty queue you have not
 * fetched yet is the one failure mode this chip has.
 */
export function isReviewCleared(
  plan: DailyPlanSummary | undefined,
  planLoading: boolean,
  counts: Pick<StarterCounts, 'due' | 'weak'>
): boolean {
  if (planLoading || !plan) return false;
  return (counts.due ?? 0) === 0 && (counts.weak ?? 0) === 0;
}
